/** Possible region keys that can be used in the URL */
export type RegionKey = "ca" | "uk" | "au";

/**  Currently supported regions */
const supportedRegionKeys = ["ca", "uk", "au"];

/** Region key to use as a fallback if we can't work it out */
const defaultRegionKey = "ca";

/** Key to store the selected region against in local storage */
const localStorageRegionKey = "region-key";

/**
 * Whether the current URL's region prefix came from RegionProvider's fallback
 * injection (unreliable for post-auth routing) vs explicit user navigation or
 * region toggle.
 *
 * Stored in sessionStorage (NOT localStorage) so the signal is scoped to the
 * tab/window that performed the injection. Cross-tab persistence (FIRE-200)
 * caused stale `inferred` flags from a bare-URL session to bounce regional
 * invite links (e.g. `/ca/apply/CASE_CA_X`) to the wrong region on Auth0
 * callback, even when `returnTo` carried correct user intent.
 *
 * sessionStorage survives a same-tab Auth0 redirect round-trip (origin is
 * preserved before/after auth) and is not shared with new tabs, so a fresh
 * email-link click starts with no `inferred` signal and uses the normal
 * priority chain. See `.cursor/rules/auth-region-routing.mdc` for the full
 * auth-flow matrix.
 */
const sessionStorageRegionSourceKey = "region-key-source";

export type RegionKeySource = "explicit" | "inferred";

export function setRegionKeySource(source: RegionKeySource): void {
  sessionStorage.setItem(sessionStorageRegionSourceKey, source);
}

export function getRegionKeySource(): RegionKeySource | null {
  const value = sessionStorage.getItem(sessionStorageRegionSourceKey);
  if (value === "explicit" || value === "inferred") {
    return value;
  }
  return null;
}

/**
 * Migrates any stale `region-key-source` value left in localStorage from
 * before this signal was scoped to sessionStorage (FIRE-200). Removes the
 * stale entry without seeding sessionStorage so a fresh tab does not inherit
 * the obsolete `inferred` signal.
 */
export function clearLegacyLocalStorageRegionKeySource(): void {
  if (localStorage.getItem(sessionStorageRegionSourceKey) !== null) {
    localStorage.removeItem(sessionStorageRegionSourceKey);
  }
}

/**
 * One-shot handshake flag indicating that the current page load was the
 * direct result of `RegionProvider` injecting a default region prefix.
 *
 * `region-key-source` alone is insufficient because, even when scoped to a
 * single tab via sessionStorage, `inferred` can outlive the page-load it was
 * set for (e.g. user types a bare URL → injection → user navigates away
 * before login → returns and logs in normally). The handshake flag is set
 * immediately before the redirect, then consumed by RegionProvider on the
 * next load. If the flag is missing, any `inferred` value in storage is
 * stale and is promoted to `explicit`.
 */
const sessionStorageRegionJustInjectedKey = "region-key-just-injected";

export function setRegionKeyJustInjected(): void {
  sessionStorage.setItem(sessionStorageRegionJustInjectedKey, "1");
}

/**
 * Returns `true` if the just-injected handshake flag was present, and
 * removes it. Read-and-clear is atomic relative to subsequent calls so the
 * flag can be honoured exactly once.
 */
export function consumeRegionKeyJustInjected(): boolean {
  const present =
    sessionStorage.getItem(sessionStorageRegionJustInjectedKey) !== null;
  if (present) {
    sessionStorage.removeItem(sessionStorageRegionJustInjectedKey);
  }
  return present;
}

/**
 * Checks a string to see if it is a supported region.
 *
 * @param region a string to test if it is a supported region
 * @returns {boolean} true if the region is supported, false otherwise
 */
export function isSupportedRegion(region: string): region is RegionKey {
  return supportedRegionKeys.includes(region);
}

/**
 * Gets the region key from the URL. This is always the very first part of the URL.
 *
 * @returns {string} The region key from the URL
 */
export function getRegionFromUrl() {
  return window.location.pathname.split("/")[1];
}

/**
 * Derives region from hostname when using region-specific hosts.
 *
 * Invite link:  applicant.certn.co/ca/apply/CASE_CA_XXXXX/
 * Verify email: auth.certn.co/u/email-verification?ticket=...
 *
 * After verify-email, Auth0 redirects to a generic /callback URL where the
 * region is no longer in the path. If the deployment uses region-specific
 * hosts (e.g. app.ca.certn.co), this function extracts the region from the
 * hostname so the user stays in the correct regional portal.
 */
export function getRegionFromHost(hostname: string): RegionKey | undefined {
  const parts = hostname.split(".");
  if (parts.length >= 2) {
    const candidate = parts[1].toLowerCase();
    if (isSupportedRegion(candidate)) {
      return candidate;
    }
  }
  return undefined;
}

/**
 * Gets the region key from local storage. This is used to store the user's last selected region.
 *
 * @returns {string} The region key from local storage
 */
export function getRegionFromLocalStorage() {
  return localStorage.getItem(localStorageRegionKey) ?? "";
}

/**
 * Attempts to find the user's region based on their browser locale.
 *
 * If the locale is not supported, it will default to the defaultRegionKey.
 * @returns {string} The region key based on the user's locale
 */
function getRegionFromLocale() {
  const localeRegion = navigator.language.split("-")[1];

  switch (localeRegion) {
    case "GB":
      return "uk";
    case "US":
      return "ca";
    case "AU":
      return "au";
    default:
      return localeRegion ? localeRegion.toLowerCase() : defaultRegionKey;
  }
}

/**
 * Gets the region key from the URL, local storage, or the user's locale.
 *
 * If the region from the URL is not supported, it will default to the defaultRegionKey.
 * @returns {string} The region key
 */
export function getRegionKey(): RegionKey {
  let regionKey: RegionKey;

  const regionFromUrl = getRegionFromUrl();
  const regionFromLocalStorage = getRegionFromLocalStorage();
  const regionFromLocale = getRegionFromLocale();

  if (isSupportedRegion(regionFromUrl)) {
    regionKey = regionFromUrl;
  } else if (isSupportedRegion(regionFromLocalStorage)) {
    regionKey = regionFromLocalStorage;
  } else if (isSupportedRegion(regionFromLocale)) {
    regionKey = regionFromLocale;
  } else {
    regionKey = defaultRegionKey;
  }

  localStorage.setItem(localStorageRegionKey, regionKey);

  return regionKey;
}

/**
 * Region value sent as the `region` attribute on LaunchDarkly contexts.
 *
 * Must match the backend's `settings.CURRENT_REGION` (`CA`, `UK`, `AU`) so a
 * single targeting rule matches both server-side and client-side evaluations.
 */
export type LaunchDarklyRegion = Uppercase<RegionKey>;

/**
 * Converts a region key into the value sent on LaunchDarkly contexts.
 *
 * @param regionKey the region key to convert
 * @returns {LaunchDarklyRegion} The uppercased region
 */
export function toLaunchDarklyRegion(regionKey: RegionKey): LaunchDarklyRegion {
  return regionKey.toUpperCase() as LaunchDarklyRegion;
}

/**
 * Gets the current region in the format sent on LaunchDarkly contexts.
 *
 * @returns {LaunchDarklyRegion} The uppercased current region
 */
export function getLaunchDarklyRegion(): LaunchDarklyRegion {
  return toLaunchDarklyRegion(getRegionKey());
}
